<template>
  <div ref="wrapper" :id="id">
    <svg
      :width="chartWidth"
      :height="height - margin"
      class="stacked-chart"
    />
  </div>
</template>

<script>
import '@/lib/d3.min.js'
export default {
  name: 'StackedBarChart',
  props: {
    width: {
      type: [String, Number],
      default: 'inherit'
    },
    height: {
      type: [String, Number],
      default: 350
    },
    id: {
      type: String,
      required: true
    },
    data: {
      type: [Object, Array],
      default: null
    },
    sort: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      csv: null,
      chartWidth: null,
      margin: 60,
      d3Configs: {
        x: null,
        y: null,
        xAxis: null,
        yAxis: null,
        dataset: []
      }
    }
  },
  computed: {
    sortedData () {
      if (this.sort) {
        const data = [...this.data]
        const withSum = data && data.map(item => {
          return {
            ...item,
            _sum: Object.values(item.differences).reduce((a, b) => Number(a) + Number(b), 0)
          }
        })
        return withSum.sort((a, b) => (a._sum < b._sum) ? 1 : ((b._sum < a._sum) ? -1 : 0))
      } return this.data
    }
  },
  mounted () {
    if (this.width === 'inherit') {
      this.chartWidth = this.$refs.wrapper.getBoundingClientRect().width
    } else this.chartWidth = this.width
    this.render()
  },
  methods: {
    render () {
      this.initTooltip()

      const margin = this.margin
      const width = this.chartWidth - 2 * margin
      const height = this.height - 2 * margin

      let currentlyDraggedGroup = ''

      const svg = d3.select(`#${this.id} svg`).append('g')
        .attr('transform', `translate(${margin}, ${margin / 5})`)

      const columns = Object.keys(this.sortedData[0].differences)

      // Transpose the data into layers
      this.dataset = d3.layout.stack()(columns.map(changeType => {
        return this.sortedData.map(function (d) {
          return { x: d.label, y: +d.differences[changeType] }
        })
      }))

      // Set x, y and colors
      this.d3Configs.x = d3.scale.ordinal()
        .domain(this.dataset[0].map(function (d) { return d.x }))
        .rangeRoundBands([10, width - 10], 0.5)

      this.d3Configs.y = d3.scale.linear()
        .domain([0, d3.max(this.dataset, function (d) { return d3.max(d, function (d) { return d.y0 + d.y }) })])
        .range([height, 0])

      // Define and draw axes
      this.yAxis = d3.svg.axis()
        .scale(this.d3Configs.y)
        .orient('left')
        .ticks(5)
        .tickSize(-width, 0, 0)
        .tickFormat(function (d) { return d })

      this.xAxis = d3.svg.axis()
        .scale(this.d3Configs.x)
        .orient('bottom')
        .ticks(0)
        .tickSize(0, 0, 0)
        .tickFormat(function (d) { return d })

      svg.append('g')
        .transition()
        .duration(800)
        .attr('class', 'y-scale')
        .call(this.yAxis)

      svg.append('g')
        .attr('class', 'x-scale')
        .attr('transform', 'translate(0,' + height + ')')
        .call(this.xAxis)

      // Create groups for each series, rects for each segment
      const groups = svg.selectAll('g.cost')
        .data(this.dataset)
        .enter()
        .append('g')
        .attr('class', function (d, i) {
          return 'rect-' + columns[i] + ' layer'
        })
        .on('mouseover', function (d, i) {
          currentlyDraggedGroup = columns[i]
        })

      // Tooltip
      const tooltip = d3.select('.stack-tooltip')

      const rect = groups.selectAll('rect')
        .data(function (d) { return d })
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', (d) => { return this.d3Configs.x(d.x) })
        .attr('y', (d) => {
          return this.d3Configs.y(d.y0 + d.y) || 0
        })
        .attr('height', (d) => { return this.d3Configs.y(d.y0) - this.d3Configs.y(d.y0 + d.y) || 0 })
        .attr('width', this.d3Configs.x.rangeBand())
        .on('mouseover', function () { tooltip.style('display', null) })
        .on('mouseout', function () { tooltip.style('display', 'none') })
        .on('mouseenter', (d, i) => {
          this.handleMouseEnter(tooltip, i, d3.event, currentlyDraggedGroup)
        })
        .on('mouseleave', (d) => {
          this.handleMouseLeave(tooltip, d)
        })

      this.renderLegends()
    },
    reRender () {
      const chart = d3.select(`#${this.id} svg`).node()
      if (!chart) return
      chart.innerHTML = ''
      this.render()
    },
    renderLegends () {
      const ratio = 7
      const itemWidth = 100
      const columns = ['added', 'changed', 'deleted', 'unchanged']

      const svg = d3.select(`#${this.id} svg`)
        .append('g')
        .attr('class', 'legends')
        .attr('transform', () => { return `translate(50, ${this.height - this.margin - 15})` })

      svg.selectAll('legends')
        .data(columns)
        .enter()
        .append('text')
        .attr('transform', function (d, i) { return `translate(${itemWidth * i + (ratio * 3)}, 0)` })
        .attr('class', function (d, i) { return 'legend legend-' + columns[i] })
        .text(function (d) { return d })
        .attr('text-anchor', 'left')
        .style('alignment-baseline', 'middle')

      svg.selectAll('legends')
        .data(columns)
        .enter()
        .append('circle')
        .attr('r', ratio)
        .attr('transform', function (d, i) { return `translate(${(itemWidth * i) + 7}, 0)` })
        .attr('class', function (d, i) { return 'legend legend-' + columns[i] })
        .text(function (d) { return d })
    },
    initTooltip () {
      let tooltip
      if (!d3.select('.stack-tooltip').empty()) {
        tooltip = d3.select('.stack-tooltip')
      } else {
        tooltip = d3.select('body').append('div').attr('class', 'stack-tooltip')
      }
    },
    handleMouseEnter (tooltip, index, event, target) {
      const barNode = event.target.getBoundingClientRect()
      const tooltipNode = tooltip.node().getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      if (!tooltip.classed('show')) {
        tooltip.classed('show', true)
      }

      const data = { [target]: this.sortedData[index].differences[target] }

      this.renderTooltipContent(tooltip, data)
      tooltip
        .style('left', barNode.left + barNode.width + 20 + 'px')
        .style('top', barNode.y + scrollTop + (barNode.height - tooltipNode.height) / 2 + 'px')
    },
    handleMouseLeave (tooltip) {
      tooltip.classed('show', false)
    },
    renderTooltipContent (tooltip, data) {
      tooltip.append(() => {
        tooltip.node().innerHTML = ''
        const ul = document.createElement('ul')
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key) && key !== 'disabled') {
            const li = document.createElement('li')
            li.innerHTML = `<span class="stack-tooltip__key">${key}:</span> ${data[key]}`
            ul.append(li)
          }
        }
        return ul
      })
    }
  },
  watch: {
    data () {
      this.reRender()
    }
  }
}
</script>

<style lang="scss">
  .stacked-chart {
    user-select: none;
    .y-scale {
      path, line {
        stroke: rgba(0, 0, 0, 0.07);
      }
      text {
        fill: #7c7c7c;
        font-weight: bold;
        font-size: 12px;
      }
    }
    .x-scale {
      path, line {
        stroke: rgba(0, 0, 0, 0.07);
      }
      text {
        fill: #7c7c7c;
        font-weight: bold;
        font-size: 12px;
      }
    }
    .bar {
      cursor: pointer;
      transition: 1s;
      &.inactive {
        opacity: 0.5;
      }
      &.disabled {
        fill: #ccc !important;
        opacity: 0.2;
      }
    }
    .grid {
      color: rgba(0, 0, 0, 0.07);
      .tick {
        color: rgba(0, 0, 0, 0.07);
      }
    }
    .rect {
      &-added {
        fill: $secondary-color;
      }
      &-changed {
        fill: $primary-color;
      }
      &-deleted {
        fill: $light-gray-color;
      }
      &-unchanged {
        fill: $extra-light-gray-color;
      }
    }
  }
  .stack-tooltip {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    background: #fff;
    border-radius: 5px;
    min-width: 150px;
    -webkit-box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    margin: 0 -15px;
    padding: 15px;
    transition: all 0.3s;
    &:after {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 0;
      height: 0;
      border: 7px solid transparent;
      border-right-color: #fff;
      border-left: 0;
      margin-top: -7px;
      margin-left: -7px;
    }
    &.show {
      opacity: 1;
      visibility: visible;
    }
    ul {
      padding: 0;
      margin: 0;
      list-style-type: none;
      li {
        font-size: 13px;
        font-weight: 600;
        color: $black-color;
      }
    }
    &__key {
      text-transform: capitalize;
      font-weight: bold;
    }
  }
  text.legend {
    text-transform: capitalize;
    font-size: 12px;
    fill: #acacac;
  }
  circle.legend {
    text-transform: capitalize;
    &-added {
      fill: $secondary-color;
    }
    &-changed {
      fill: $primary-color;
    }
    &-deleted {
      fill: $light-gray-color;
    }
    &-unchanged {
      fill: $extra-light-gray-color;
    }
  }
</style>
